/**
 * Cross-app auth sync.
 *
 * MJ-Extended-PM, MJ-Tracker and MJ-Estimator share one Entra ID app
 * registration, so signing in to one signs you in to the others (the AAD
 * session cookie is shared). Signing *out* is not shared: each app is a
 * separate origin with its own localStorage, so `logoutRedirect()` only clears
 * the origin it was called from. The other apps keep serving their cached
 * tokens — and after a logout + login as a different user, keep running as the
 * *previous* account.
 *
 * All the apps are subdomains of one registrable domain (`waypointapp.co.uk`),
 * so a cookie set on the shared parent domain is first-party for all of them.
 * We keep the currently signed-in account's `homeAccountId` there; each app
 * compares it against its own cached account (see hooks/useAuthSync) and resets
 * itself when they differ.
 */

// The configured parent domain, e.g. ".qa.waypointapp.co.uk". Empty for local dev.
const configuredDomain = (import.meta.env.VITE_AUTH_COOKIE_DOMAIN ?? "")
  .trim()
  .replace(/^\./, "");

const hostname = window.location.hostname;

const hostIsUnder = (domain: string) =>
  hostname === domain || hostname.endsWith(`.${domain}`);

/**
 * A cookie can only be scoped to a domain the current host sits under; the
 * browser drops the write silently otherwise. So a domain this host isn't
 * under — localhost, or a build deployed to a different environment's host —
 * falls back to a host-only cookie: the sync degrades to "no cross-app sync on
 * this host" instead of failing invisibly.
 */
const cookieScope =
  configuredDomain && hostIsUnder(configuredDomain) ? configuredDomain : null;

const isLocalHost = ["localhost", "127.0.0.1", "[::1]"].includes(hostname);

if (configuredDomain && !cookieScope && !isLocalHost) {
  console.warn(
    `[authSync] VITE_AUTH_COOKIE_DOMAIN is "${configuredDomain}" but this app is ` +
      `served from "${hostname}", which is not under it. Falling back to a ` +
      `host-only cookie — cross-app logout sync is disabled on this host.`
  );
}

/**
 * Named after the domain the cookie lives on rather than the environment label,
 * for two reasons.
 *
 * First, a suffix is needed at all because cookies are visible to every
 * subdomain *below* the domain they are set on: a cookie on
 * `.waypointapp.co.uk` is also readable on `.qa.waypointapp.co.uk`, so without
 * distinct names a production logout would sign people out of QA.
 *
 * Second, the apps that must agree on the name are exactly the ones sharing a
 * domain — and each repo labels its environments differently (this workspace's
 * `staging` and MJ-Estimator's `uat` are both `uat.waypointapp.co.uk`).
 * Deriving the name from the domain keeps the apps in sync without a naming
 * convention anyone has to remember.
 */
const COOKIE_NAME = `mj_active_account_${
  cookieScope ? cookieScope.replace(/[^a-zA-Z0-9]/g, "_") : "local"
}`;

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24;

/**
 * Written on logout instead of deleting the cookie. A *missing* cookie is
 * ambiguous — it also means "this browser has never written one" (first load
 * after this shipped) — and treating that as a logout would sign every existing
 * user out once. An explicit tombstone is unambiguous.
 */
export const LOGGED_OUT = "none";

// `secure` only alongside a domain: without one we are on localhost over plain
// http. Cookies ignore port, so a host-only cookie is still shared between the
// apps running on different localhost ports.
const cookieAttributes = cookieScope
  ? `path=/; samesite=lax; secure; domain=.${cookieScope}`
  : "path=/; samesite=lax";

const setCookie = (value: string) => {
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; ${cookieAttributes}; max-age=${COOKIE_MAX_AGE_SECONDS}`;
};

/** The account the other apps on this browser believe is signed in, if any. */
export const readSharedAccount = (): string | null => {
  const prefix = `${COOKIE_NAME}=`;
  const entry = document.cookie
    .split("; ")
    .find((candidate) => candidate.startsWith(prefix));

  return entry ? decodeURIComponent(entry.slice(prefix.length)) : null;
};

/** Publish the signed-in account to the sibling apps. */
export const writeSharedAccount = (homeAccountId: string) => {
  setCookie(homeAccountId);
};

/** Tell the sibling apps this browser has signed out. */
export const markSharedLogout = () => {
  setCookie(LOGGED_OUT);
};
